import React, { useState, useEffect, useRef, useMemo } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Config from "../../../Config";
import Loading from "../../../Components/Loading";
import {
    BoxTitle,
    BoxContainer,
    BoxHead,
    Underline,
} from "../../../Components/Styles/PageStyles";
import { useSelector } from "react-redux";
import PropertiesFilterDropdown from "../../../Components/PropertiesFilterDropdown";
import { format } from "date-fns";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { MdKeyboardArrowDown, MdOutlineChevronRight, MdOutlineFastfood } from "react-icons/md";
import { FaAppleAlt, FaArrowLeft, FaArrowRight, FaCoffee, FaConciergeBell, FaDrumstickBite, FaCookieBite, FaRegCalendarAlt, FaUtensils } from "react-icons/fa";
import tw from "tailwind-styled-components";

const MealSummary = () => {
    const properties = useSelector((state) => state.PropertyReducer.property);
    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty,
    );
    const user = useSelector((state) => state.UserReducer.user);
    const [selectedProperty, setSelectedProperty] = useState(property);
    const [selectedDate, setSelectedDate] = useState(new Date());

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [apiData, setApiData] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const [error, setError] = useState(null);
    const calendarRef = useRef(null);
    const navigate = useNavigate();

    const handlePropertySelect = (property) => {
        setSelectedProperty(property);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setIsCalendarOpen(false);
        getListMutate(date);
    };

    const toggleCalendar = () => {
        setIsCalendarOpen(!isCalendarOpen);
    };

    const fetchFunction = async (date) => {
        const formattedDate = format(date || selectedDate, "yyyy-MM-dd");
        const response = await axios.get(
            `${Config.apiUrl}/food/tenant/meal/selection/summary?propId=${selectedProperty.id}&mealDate=${formattedDate}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            },
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.data === false) {
            setError(true);
            setApiData(null);
        } else {
            setApiData(res?.data);
            setIsFocused(true);
            setError(null);
        }
    };

    const getError = (err) => {
        setError(err);
        console.error("Error fetching meal data:", err);
    };

    const {
        data: mealData,
        isLoading,
        mutate: getListMutate,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    // Navigation handler with number values
    const handleNavigateToParticipants = (mealType, dietPreference = null) => {
        const formattedDate = format(selectedDate, "yyyy-MM-dd");
        const params = new URLSearchParams({
            mealDate: formattedDate,
            mealType: mealType,
            propertyId: selectedProperty.id
        });

        if (dietPreference !== null) {
            params.append('dietPreference', dietPreference);
        }

        navigate(`/meal-participants?${params.toString()}`);
    };

    const getMealTypeNumber = (mealName) => {
        const mealMap = {
            'breakfast': Config.MEAL_TYPE.BREAKFAST,
            'lunch': Config.MEAL_TYPE.LUNCH,
            'snacks': Config.MEAL_TYPE.SNACKS,
            'dinner': Config.MEAL_TYPE.DINNER
        };
        return mealMap[mealName.toLowerCase()];
    };

    useEffect(() => {
        if (selectedProperty && user?.token) {
            getListMutate(selectedDate);
        }
    }, [selectedProperty, user?.token]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsCalendarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // const CustomCalendar = () => (
    //     <Calendar
    //         onChange={handleDateChange}
    //         value={selectedDate}
    //         className="w-full"
    //         prevLabel={<FaArrowLeft className="text-gray-600 hover:text-blue-600" />}
    //         nextLabel={<FaArrowRight className="text-gray-600 hover:text-blue-600" />}
    //         prev2Label={null}
    //         next2Label={null}
    //         tileClassName={({ date }) => {
    //             const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
    //             const isSelected = format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
    //             if (isSelected) {
    //                 return "bg-blue-500 text-white rounded-full hover:bg-blue-600";
    //             }
    //             if (isToday) {
    //                 return "bg-blue-100 rounded-full hover:bg-blue-200";
    //             }
    //             return "";
    //         }}
    //     />
    // );

    const CustomCalendar = () => (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">

            {/* Header */}

            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">

                <div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-primary-color">
                        Food Calendar
                    </p>

                    <h3 className="mt-1 text-lg font-semibold text-gray-900">
                        {format(selectedDate, "EEEE, dd MMM yyyy")}
                    </h3>

                </div>

                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-primary-color">
                    Today: {format(new Date(), "dd MMM")}
                </span>

            </div>

            {/* Calendar */}

            <div className="p-4">

                <Calendar
                    onChange={handleDateChange}
                    value={selectedDate}
                    className="premium-calendar"

                    prevLabel={
                        <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:border-primary-color hover:bg-blue-50 hover:text-primary-color">
                            <FaArrowLeft size={14} />
                        </button>
                    }

                    nextLabel={
                        <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:border-primary-color hover:bg-blue-50 hover:text-primary-color">
                            <FaArrowRight size={14} />
                        </button>
                    }

                    prev2Label={null}
                    next2Label={null}

                    tileClassName={({ date }) => {

                        const isToday =
                            format(date, "yyyy-MM-dd") ===
                            format(new Date(), "yyyy-MM-dd");

                        const isSelected =
                            format(date, "yyyy-MM-dd") ===
                            format(selectedDate, "yyyy-MM-dd");

                        if (isSelected) return "calendar-selected";
                        if (isToday) return "calendar-today";

                        return "calendar-day";
                    }}
                />

            </div>

        </div>
    );

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Meal Summary
                    <Underline />
                </BoxTitle>
                <div className="flex items-center gap-4 flex-wrap">
                    <PropertiesFilterDropdown
                        properties={properties}
                        selectedProperty={selectedProperty}
                        onPropertySelect={handlePropertySelect}
                        placeholder="Select Property"
                        className="min-w-[200px] w-full md:w-[220px]"
                    />

                    <div className="relative" ref={calendarRef}>
                        <div
                            className="flex items-center gap-2 px-3 h-10 bg-white border rounded-md cursor-pointer hover:border-blue-500 transition-all duration-200 min-w-[200px] hover:shadow-md"
                            onClick={toggleCalendar}
                        >
                            <FaRegCalendarAlt className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-800 flex-1">
                                {format(selectedDate, "MMM d, yyyy")}
                            </span>
                            <MdKeyboardArrowDown
                                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isCalendarOpen ? 'rotate-180' : ''
                                    }`}
                            />
                        </div>

                        {/* Calendar Dropdown */}
                        {isCalendarOpen && (
                            <div className="absolute right-0 mt-2 z-50 bg-white rounded-lg shadow-xl border p-3 min-w-[340px]">
                                <CustomCalendar />
                            </div>
                        )}
                    </div>
                </div>
            </BoxHead>

            {/* Loading State */}
            {(isLoading || isRefetching) && (
                <Loading />
            )}

            {/* Meal Cards Grid */}
            {!isLoading && !isRefetching && apiData && (
                <div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                        {["breakfast", "lunch", "snacks", "dinner"].map((meal) => {

                            const total = Number(apiData?.[meal] || 0);
                            const consumed = Number(apiData?.[`${meal}Consumed`] || 0);
                            const pending = Math.max(total - consumed, 0);
                            const percentage = total ? Math.round((consumed / total) * 100) : 0;

                            const mealData = {
                                breakfast: {
                                    icon: <MdOutlineFastfood />,
                                    iconBg: "bg-amber-50",
                                    iconColor: "text-amber-600",
                                    progress: "bg-amber-500",
                                },
                                lunch: {
                                    icon: <FaUtensils />,
                                    iconBg: "bg-sky-50",
                                    iconColor: "text-sky-600",
                                    progress: "bg-sky-500",
                                },
                                snacks: {
                                    icon: <FaCookieBite />,
                                    iconBg: "bg-indigo-50",
                                    iconColor: "text-indigo-600",
                                    progress: "bg-indigo-500",
                                },
                                dinner: {
                                    icon: <FaConciergeBell />,
                                    iconBg: "bg-violet-50",
                                    iconColor: "text-violet-600",
                                    progress: "bg-violet-500",
                                },
                            };

                            const current = mealData[meal];
                            const mealTypeNumber = getMealTypeNumber(meal);

                            return (
                                <div
                                    key={meal}
                                    onClick={() => handleNavigateToParticipants(mealTypeNumber)}
                                    className="group cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                >

                                    {/* Header */}

                                    <div className="flex items-center justify-between">

                                        <div className="flex items-center gap-4">

                                            <div
                                                className={`w-14 h-14 rounded-xl ${current.iconBg} ${current.iconColor} flex items-center justify-center text-2xl transition-transform group-hover:scale-110`}
                                            >
                                                {current.icon}
                                            </div>

                                            <div>

                                                <h2 className="text-lg font-semibold text-gray-900 capitalize">
                                                    {meal}
                                                </h2>

                                                <p className="text-sm text-gray-500">
                                                    Meal Statistics
                                                </p>

                                            </div>

                                        </div>

                                        <MdOutlineChevronRight
                                            className="text-gray-400 group-hover:text-[#0078BD] group-hover:translate-x-1 transition"
                                            size={24}
                                        />

                                    </div>

                                    {/* Main KPI */}

                                    <div className="mt-6">

                                        <div className="text-4xl font-bold text-gray-900">
                                            {consumed}
                                        </div>

                                        <div className="text-sm text-gray-500 mt-1">
                                            Meals Consumed
                                        </div>

                                    </div>

                                    {/* Stats */}

                                    <div className="flex justify-between mt-6">

                                        <div>

                                            <p className="text-xs uppercase tracking-wide text-gray-400">
                                                Ordered
                                            </p>

                                            <p className="text-lg font-semibold text-gray-900">
                                                {total}
                                            </p>

                                        </div>

                                        <div className="text-right">

                                            <p className="text-xs uppercase tracking-wide text-gray-400">
                                                Pending
                                            </p>

                                            <p className="text-lg font-semibold text-orange-500">
                                                {pending}
                                            </p>

                                        </div>

                                    </div>

                                    {/* Progress */}

                                    <div className="mt-5">

                                        <div className="flex justify-between text-xs text-gray-500 mb-2">

                                            <span>Consumption</span>

                                            <span>{percentage}%</span>

                                        </div>

                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">

                                            <div
                                                className={`h-full rounded-full ${current.progress}`}
                                                style={{
                                                    width: `${percentage}%`,
                                                }}
                                            />

                                        </div>

                                    </div>

                                </div>
                            );
                        })}
                    </div>


                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pb-6">

                        {["lunch", "dinner"].map((meal) => {

                            const mealTypeNumber = getMealTypeNumber(meal);

                            const mealIcon =
                                meal === "lunch"
                                    ? <FaUtensils />
                                    : <FaConciergeBell />;

                            return (

                                <div
                                    key={meal}
                                    className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
                                >

                                    {/* Header */}

                                    <div className="flex items-center gap-3 mb-6">

                                        <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#0078BD] flex items-center justify-center text-xl">
                                            {mealIcon}
                                        </div>

                                        <div>

                                            <h2 className="text-xl font-semibold capitalize">
                                                {meal}
                                            </h2>

                                            <p className="text-sm text-gray-500">
                                                Diet Preference Summary
                                            </p>

                                        </div>

                                    </div>

                                    <div className="grid md:grid-cols-2 gap-5">

                                        {[
                                            {
                                                title: "Veg",
                                                color: "green",
                                                ordered: Number(apiData?.[`${meal}Veg`] || 0),
                                                consumed: Number(apiData?.[`${meal}VegConsumed`] || 0),
                                                diet: Config.DIET_PREFERENCE.VEG,
                                            },
                                            {
                                                title: "Non-Veg",
                                                color: "red",
                                                ordered: Number(apiData?.[`${meal}NonVeg`] || 0),
                                                consumed: Number(apiData?.[`${meal}NonVegConsumed`] || 0),
                                                diet: Config.DIET_PREFERENCE.NON_VEG,
                                            },
                                        ].map((item) => {

                                            const pending = item.ordered - item.consumed;

                                            const percentage = item.ordered
                                                ? Math.round(item.consumed * 100 / item.ordered)
                                                : 0;

                                            return (

                                                <div
                                                    key={item.title}
                                                    onClick={() =>
                                                        handleNavigateToParticipants(
                                                            mealTypeNumber,
                                                            item.diet
                                                        )
                                                    }
                                                    className="cursor-pointer rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:shadow-md transition p-5"
                                                >

                                                    <div className="flex justify-between items-center">

                                                        <div className="flex items-center gap-2">

                                                            <span
                                                                className={`w-3 h-3 rounded-full bg-${item.color}-500`}
                                                            />

                                                            <h4
                                                                className={`font-semibold text-${item.color}-600`}
                                                            >
                                                                {item.title}
                                                            </h4>

                                                        </div>

                                                        <MdOutlineChevronRight
                                                            className="text-gray-400"
                                                        />

                                                    </div>

                                                    <div className="mt-5">

                                                        <div className="text-3xl font-bold">
                                                            {item.consumed}
                                                        </div>

                                                        <div className="text-sm text-gray-500">
                                                            Meals Consumed
                                                        </div>

                                                    </div>

                                                    <div className="flex justify-between mt-5 text-sm">

                                                        <div>

                                                            <div className="text-gray-400">
                                                                Ordered
                                                            </div>

                                                            <div className="font-semibold">
                                                                {item.ordered}
                                                            </div>

                                                        </div>

                                                        <div className="text-right">

                                                            <div className="text-gray-400">
                                                                Pending
                                                            </div>

                                                            <div className="font-semibold text-orange-500">
                                                                {pending}
                                                            </div>

                                                        </div>

                                                    </div>

                                                    <div className="mt-5">

                                                        <div className="flex justify-between text-xs mb-2">

                                                            <span className="text-gray-500">
                                                                Consumption
                                                            </span>

                                                            <span className="font-medium">
                                                                {percentage}%
                                                            </span>

                                                        </div>

                                                        <div className="h-2 rounded-full bg-gray-200">

                                                            <div
                                                                className={`h-2 rounded-full bg-${item.color}-500`}
                                                                style={{
                                                                    width: `${percentage}%`,
                                                                }}
                                                            />

                                                        </div>

                                                    </div>

                                                </div>

                                            );

                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </BoxContainer>
    );
};

export default MealSummary;